export interface SheetTable {
	/** Sheet or tab name, empty for a single-table file. */
	name: string
	rows: string[][]
	/** Rows the file holds beyond the ones parsed here. */
	truncatedRows: number
}

/** A preview is a glance, not the file: enough rows to judge, never the lot. */
export const PREVIEW_ROW_LIMIT = 100
export const PREVIEW_COLUMN_LIMIT = 30

/**
 * Reads CSV or TSV without a dependency, following RFC 4180: quoted fields may
 * hold the delimiter, a newline, or a doubled quote.
 */
export function parseDelimited(
	text: string,
	options: { delimiter?: string; rowLimit?: number } = {},
): SheetTable {
	const rowLimit = options.rowLimit ?? PREVIEW_ROW_LIMIT
	const delimiter = options.delimiter ?? detectDelimiter(text)

	const rows: string[][] = []
	let row: string[] = []
	let field = ""
	let quoted = false

	const endField = () => {
		row.push(field)
		field = ""
	}
	const endRow = () => {
		endField()
		rows.push(row)
		row = []
	}

	const body = text.replace(/^\uFEFF/, "")

	for (let i = 0; i < body.length; i++) {
		const char = body[i]

		if (quoted) {
			if (char === '"') {
				if (body[i + 1] === '"') {
					field += '"'
					i++
				} else {
					quoted = false
				}
			} else {
				field += char
			}
			continue
		}

		if (char === '"' && field === "") {
			quoted = true
		} else if (char === delimiter) {
			endField()
		} else if (char === "\n") {
			endRow()
		} else if (char === "\r") {
			// Swallow, the \n that follows closes the row.
		} else {
			field += char
		}
	}

	if (field !== "" || row.length > 0) endRow()

	// A trailing newline leaves one empty row that means nothing.
	while (rows.length > 0) {
		const last = rows[rows.length - 1]
		if (last && last.length === 1 && last[0] === "") rows.pop()
		else break
	}

	return clampTable({ name: "", rows, truncatedRows: 0 }, rowLimit)
}

function detectDelimiter(text: string): string {
	const firstLine = text.split(/\r?\n/, 1)[0] ?? ""
	const tabs = (firstLine.match(/\t/g) ?? []).length
	const semicolons = (firstLine.match(/;/g) ?? []).length
	const commas = (firstLine.match(/,/g) ?? []).length
	if (tabs > commas && tabs >= semicolons) return "\t"
	if (semicolons > commas) return ";"
	return ","
}

/** Trims a table to what the preview shows and reports what it left behind. */
export function clampTable(
	table: SheetTable,
	rowLimit = PREVIEW_ROW_LIMIT,
	columnLimit = PREVIEW_COLUMN_LIMIT,
): SheetTable {
	const kept = table.rows.slice(0, rowLimit).map((row) => {
		const cells = row.slice(0, columnLimit)
		// A ragged row would break the table, so pad to the widest kept row.
		return cells
	})
	const width = kept.reduce((max, row) => Math.max(max, row.length), 0)
	return {
		name: table.name,
		rows: kept.map((row) =>
			row.length === width
				? row
				: [...row, ...Array<string>(width - row.length).fill("")],
		),
		truncatedRows: Math.max(0, table.rows.length - kept.length),
	}
}
